import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  User,
  Cog,
  Check,
  X,
  CheckCircle2,
  Loader2,
  Pencil,
  Trash2,
  CalendarClock,
  ClipboardCheck,
  Plus,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  scheduled: { label: "Scheduled", color: "bg-blue-200 text-blue-800", calendarColor: "bg-blue-500" },
  pending: { label: "Pending", color: "bg-amber-200 text-amber-800", calendarColor: "bg-amber-500" },
  confirmed: { label: "Confirmed", color: "bg-emerald-200 text-emerald-800", calendarColor: "bg-emerald-500" },
  overdue: { label: "Overdue", color: "bg-red-200 text-red-800", calendarColor: "bg-red-500" },
  completed: { label: "Completed", color: "bg-slate-200 text-slate-700", calendarColor: "bg-slate-400" },
  cancelled: { label: "Cancelled", color: "bg-slate-200 text-slate-500", calendarColor: "bg-slate-300" },
};

const ServiceCalendar = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [viewMode, setViewMode] = useState("month"); // month, week, list
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedWorkerId, setSelectedWorkerId] = useState("");
  
  // Reschedule state
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({ booking_date: "", start_time: "", end_time: "" });
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchBookings = async () => {
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const [bookingsRes, usersRes] = await Promise.all([
        axios.get(
          `${API}/service-bookings/calendar?month=${month}&year=${year}`,
          { withCredentials: true }
        ),
        axios.get(`${API}/users/workers`, { withCredentials: true }),
      ]);
      setBookings(bookingsRes.data);
      setWorkers(usersRes.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [currentDate]);

  const handleAssignWorker = async () => {
    if (!selectedBooking || !selectedWorkerId) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/service-bookings/${selectedBooking.booking_id}/assign-worker?worker_id=${selectedWorkerId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Technician assigned successfully");
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign technician");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAction = async (action) => {
    if (!selectedBooking) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/service-bookings/${selectedBooking.booking_id}/${action}`,
        {},
        { withCredentials: true }
      );
      toast.success(
        action === "confirm"
          ? "Booking confirmed"
          : action === "complete"
          ? "Booking completed"
          : "Booking cancelled"
      );
      setDialogOpen(false);
      fetchBookings();
    } catch (error) {
      console.error(`Error ${action} booking:`, error);
      toast.error(error.response?.data?.detail || `Failed to ${action} booking`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedBooking || !rescheduleData.booking_date || !rescheduleData.start_time || !rescheduleData.end_time) {
      toast.error("Please fill in all reschedule fields");
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/service-bookings/${selectedBooking.booking_id}/reschedule`,
        {
          booking_date: rescheduleData.booking_date,
          time_slot: {
            start_time: rescheduleData.start_time,
            end_time: rescheduleData.end_time
          }
        },
        { withCredentials: true }
      );
      toast.success("Booking rescheduled - customer notified");
      setRescheduleDialogOpen(false);
      setDialogOpen(false);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reschedule booking");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBooking) return;
    setActionLoading(true);
    try {
      await axios.delete(
        `${API}/service-bookings/${selectedBooking.booking_id}`,
        { withCredentials: true }
      );
      toast.success("Booking deleted - customer notified");
      setDeleteDialogOpen(false);
      setDialogOpen(false);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete booking");
    } finally {
      setActionLoading(false);
    }
  };

  const openRescheduleDialog = () => {
    if (selectedBooking) {
      setRescheduleData({
        booking_date: selectedBooking.booking_date || "",
        start_time: selectedBooking.time_slot?.start_time || "",
        end_time: selectedBooking.time_slot?.end_time || ""
      });
      setRescheduleDialogOpen(true);
    }
  };

  const navigateMonth = (delta) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + delta);
      return newDate;
    });
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    // Add empty cells for days before the first
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getBookingsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return bookings.filter((b) => b.booking_date === dateStr);
  };

  const filteredBookings =
    statusFilter === "all"
      ? bookings
      : bookings.filter((b) => b.status === statusFilter);

  const pendingCount = bookings.filter((b) => b.status === "pending").length;

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-96 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="service-calendar-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            Service Calendar
          </h1>
          <p className="text-slate-500 mt-1">
            Manage customer service bookings
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/add-customer-service">
            <Button className="rounded-sm bg-indigo-600 hover:bg-indigo-700" data-testid="new-booking-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Booking
            </Button>
          </Link>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 rounded-sm">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Pending Alert */}
      {pendingCount > 0 && (
        <Card className="rounded-sm border-amber-200 bg-amber-50 mb-6">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">
                {pendingCount} booking{pendingCount > 1 ? "s" : ""} pending confirmation
              </p>
              <p className="text-sm text-amber-600">
                Review and confirm customer booking requests
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar Navigation */}
      <Card className="rounded-sm border-slate-200 mb-6">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigateMonth(-1)}
              className="rounded-sm"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigateMonth(1)}
              className="rounded-sm"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map((day) => (
              <div
                key={day}
                className="text-center text-sm font-medium text-slate-500 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 auto-rows-min">
            {getDaysInMonth().map((day, index) => {
              const dayBookings = getBookingsForDay(day);
              const filteredDayBookings = dayBookings.filter(
                (b) => statusFilter === "all" || b.status === statusFilter
              );
              const isToday =
                day &&
                new Date().toDateString() ===
                  new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    day
                  ).toDateString();

              return (
                <div
                  key={`${currentDate.getFullYear()}-${currentDate.getMonth()}-${day ?? `pad-${index}`}`}
                  className={`min-h-[70px] p-1 border rounded-sm ${
                    day ? "bg-white" : "bg-slate-50"
                  } ${isToday ? "border-indigo-500 border-2" : "border-slate-200"}`}
                >
                  {day && (
                    <>
                      <div
                        className={`text-sm font-medium mb-1 ${
                          isToday ? "text-indigo-600" : "text-slate-700"
                        }`}
                      >
                        {day}
                      </div>
                      <div className="space-y-1">
                        {filteredDayBookings.map((booking) => (
                            <div
                              key={booking.booking_id}
                              onClick={() => {
                                setSelectedBooking(booking);
                                setSelectedWorkerId(booking.assigned_worker_id || "");
                                setDialogOpen(true);
                              }}
                              className="flex items-center gap-1 text-xs p-1 rounded cursor-pointer hover:bg-slate-100 group"
                              title={`${booking.customer_name} - ${booking.service_name || 'Service'} (${statusConfig[booking.status]?.label || booking.status})`}
                            >
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusConfig[booking.status]?.calendarColor || 'bg-slate-400'}`}></span>
                              <span className="truncate">{booking.time_slot.start_time} {booking.customer_name}</span>
                            </div>
                          ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* List View */}
      <Card className="rounded-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">
            Bookings This Month ({filteredBookings.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredBookings.length > 0 ? (
            <div className="divide-y divide-slate-200">
              {filteredBookings
                .sort((a, b) => b.booking_date.localeCompare(a.booking_date))
                .map((booking) => (
                  <div
                    key={booking.booking_id}
                    className="p-4 hover:bg-slate-50 cursor-pointer"
                    onClick={() => {
                      setSelectedBooking(booking);
                      setDialogOpen(true);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="text-center min-w-[60px]">
                          <p className="text-2xl font-semibold text-slate-900">
                            {new Date(booking.booking_date).getDate()}
                          </p>
                          <p className="text-xs text-slate-500">
                            {monthNames[new Date(booking.booking_date).getMonth()].slice(0, 3)}
                          </p>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {booking.customer_name}
                          </p>
                          {booking.venue_name && (
                            <p className="text-xs text-blue-600 flex items-center gap-1">
                              <MapPin className="h-3 w-3" />{booking.venue_name}
                            </p>
                          )}
                          <p className="text-sm text-slate-500">
                            {booking.installation_name}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                            <Clock className="h-3.5 w-3.5" />
                            {booking.time_slot.start_time} -{" "}
                            {booking.time_slot.end_time}
                          </div>
                        </div>
                      </div>
                      <Badge
                        className={`rounded-sm ${
                          statusConfig[booking.status]?.color || "bg-slate-100"
                        }`}
                      >
                        {statusConfig[booking.status]?.label || booking.status}
                      </Badge>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No bookings this month</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) setSelectedWorkerId("");
      }}>
        <DialogContent className="sm:max-w-2xl w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
            <DialogDescription>
              {selectedBooking?.booking_date} at{" "}
              {selectedBooking?.time_slot?.start_time} -{" "}
              {selectedBooking?.time_slot?.end_time}
            </DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Status</span>
                <Badge
                  className={`rounded-sm ${
                    statusConfig[selectedBooking.status]?.color || "bg-slate-100"
                  }`}
                >
                  {statusConfig[selectedBooking.status]?.label ||
                    selectedBooking.status}
                </Badge>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-400" />
                  <span className="font-medium">{selectedBooking.customer_name}</span>
                </div>
                {selectedBooking.venue_name && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600">{selectedBooking.venue_name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Cog className="h-4 w-4 text-slate-400" />
                  <span>{selectedBooking.installation_name}</span>
                </div>
                {selectedBooking.service_name && (
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-slate-400" />
                    <span>Service: {selectedBooking.service_name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span>{selectedBooking.booking_date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span>
                    {selectedBooking.time_slot.start_time} -{" "}
                    {selectedBooking.time_slot.end_time}
                  </span>
                </div>
              </div>
              
              {/* Worker Assignment */}
              {selectedBooking && !["completed", "cancelled"].includes(selectedBooking.status) && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Assign Technician</p>
                  <div className="flex gap-2">
                    <Select
                      value={selectedWorkerId}
                      onValueChange={setSelectedWorkerId}
                    >
                      <SelectTrigger className="rounded-sm flex-1" data-testid="worker-select">
                        <SelectValue placeholder="Select technician" />
                      </SelectTrigger>
                      <SelectContent>
                        {workers.map((worker) => (
                          <SelectItem key={worker.user_id} value={worker.user_id}>
                            {worker.name} ({worker.role === "worker" ? "Technician" : worker.role === "manager" ? "Manager" : "Admin"})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleAssignWorker}
                      disabled={!selectedWorkerId || actionLoading}
                      className="rounded-sm"
                      size="sm"
                    >
                      {selectedBooking.assigned_worker_id ? "Reassign" : "Assign"}
                    </Button>
                  </div>
                </div>
              )}
              
              {selectedBooking.notes && (
                <div className="p-3 bg-slate-50 rounded-sm">
                  <p className="text-sm text-slate-600">{selectedBooking.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-col gap-2 sm:justify-start">
            {/* Management buttons for non-completed/cancelled bookings */}
            {selectedBooking && !["completed", "cancelled"].includes(selectedBooking.status) && (
              <div className="flex gap-2 w-full justify-start">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openRescheduleDialog}
                  disabled={actionLoading}
                  className="rounded-sm flex-1"
                >
                  <CalendarClock className="h-4 w-4 mr-1" />
                  Reschedule
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={actionLoading}
                  className="rounded-sm text-red-600 hover:text-red-700 flex-1"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            )}
            
            {/* Status action buttons */}
            {selectedBooking?.status === "pending" && (
              <div className="flex gap-2 w-full justify-start">
                <Button
                  variant="outline"
                  onClick={() => handleAction("cancel")}
                  disabled={actionLoading}
                  className="rounded-sm flex-1"
                >
                  <X className="h-4 w-4 mr-1" />
                  Decline
                </Button>
                <Button
                  onClick={() => handleAction("confirm")}
                  disabled={actionLoading}
                  className="rounded-sm bg-emerald-600 hover:bg-emerald-700 flex-1"
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  Confirm
                </Button>
              </div>
            )}
            {selectedBooking?.status === "confirmed" && (
              <div className="flex gap-2 w-full justify-start">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/job-card/${selectedBooking.booking_id}`)}
                  className="rounded-sm flex-1"
                  data-testid="view-job-card-btn"
                >
                  <ClipboardCheck className="h-4 w-4 mr-1" />
                  Job Card
                </Button>
                <Button
                  onClick={() => handleAction("complete")}
                  disabled={actionLoading}
                  className="rounded-sm bg-blue-600 hover:bg-blue-700 flex-1"
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                  )}
                  Complete
                </Button>
              </div>
            )}
            {selectedBooking?.status === "scheduled" && (
              <div className="flex gap-2 w-full justify-start">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/job-card/${selectedBooking.booking_id}`)}
                  className="rounded-sm flex-1"
                  data-testid="view-job-card-btn"
                >
                  <ClipboardCheck className="h-4 w-4 mr-1" />
                  Job Card
                </Button>
                <Button
                  onClick={() => handleAction("complete")}
                  disabled={actionLoading}
                  className="rounded-sm bg-blue-600 hover:bg-blue-700 flex-1"
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                  )}
                  Complete
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reschedule Booking</DialogTitle>
            <DialogDescription>
              Select a new date and time for this booking. The customer will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="reschedule-date">New Date</Label>
              <Input
                id="reschedule-date"
                type="date"
                value={rescheduleData.booking_date}
                onChange={(e) => setRescheduleData({ ...rescheduleData, booking_date: e.target.value })}
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="reschedule-start">Start Time</Label>
                <Input
                  id="reschedule-start"
                  type="time"
                  value={rescheduleData.start_time}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, start_time: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label htmlFor="reschedule-end">End Time</Label>
                <Input
                  id="reschedule-end"
                  type="time"
                  value={rescheduleData.end_time}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, end_time: e.target.value })}
                  className="rounded-sm"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleReschedule} disabled={actionLoading} className="rounded-sm">
              {actionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CalendarClock className="h-4 w-4 mr-2" />
              )}
              Reschedule & Notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Booking</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this booking? The customer will be notified about the cancellation.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {selectedBooking && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-4">
                <p className="text-sm font-medium text-red-800">{selectedBooking.customer_name}</p>
                <p className="text-sm text-red-600">
                  {selectedBooking.service_name || 'Service'} on {selectedBooking.booking_date}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={actionLoading} 
              className="rounded-sm"
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete & Notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceCalendar;
